import { clientApi, globalApi } from "@/utils/api";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { usePagination } from "@/hooks/usePagination";
import { createUrlWithParams } from "@/utils/url";
import { TGetCompanyListReqDto, TGetCompanyListResDto, TGetCompanyOutputDto } from "@/api/services/main/company/dtos/get-company-list.dto";
import { TCreateCompanyReqDto, TCreateCompanyResDto } from "@/api/services/main/company/dtos/create-company-req.dto";
import { useParams } from "react-router-dom";
import { TGetCompanyResDto } from "@/api/services/main/company/dtos/get-company.dto";
import { TCompanyRowExcel } from "@/features/company/types";
import { TOption } from "@/types/common";
import { useMemo } from "react";
import { TGetSingedUrlResDto } from "@/api/services/global/file/dtos/get-signed-url";

export const fetchCompanyListApi = async (req: TGetCompanyListReqDto): Promise<TGetCompanyListResDto> => {
  const url = createUrlWithParams("/auth/company", req);

  const rs = await clientApi.get<TGetCompanyListResDto>(url);
  return rs.data;
};

export const useCompanyList = (req?: TGetCompanyListReqDto) => {
  const pagReq = usePagination();
  const params = req ?? pagReq;

  const { data, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.COMPANY.FETCH_LIST, params],
    queryFn: () => fetchCompanyListApi(params),
  });

  const companyOptions: TOption[] = useMemo(
    () => (data ? data?.data.map((item) => ({ label: item.companyName ?? "", value: item.id })) : []),
    [data],
  );

  const companyHasPrefectureOptions: TOption[] = useMemo(
    () =>
      data
        ? data?.data.map((item) => {
            let label = item.companyName ?? "";
            const prefecture = item.prefecture ?? "";
            if (label !== "" && prefecture !== "") {
              label = `${label} (${prefecture})`;
            }
            return { label: label, value: item.id };
          })
        : [],
    [data],
  );

  return { data, companyOptions, companyHasPrefectureOptions, ...rest };
};

const processDataBeforeCallApi = (payload: TCreateCompanyReqDto) => {
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value) && value.length === 0) return;

    if (value instanceof File) {
      formData.append(key, value);
    } else if (Array.isArray(value)) {
      if (value.every((v) => v instanceof File)) {
        value.forEach((file) => formData.append(key, file));
      } else {
        formData.append(key, JSON.stringify(value));
      }
    } else if (typeof value === "number") {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, String(value));
    }
  });
  return formData;
};

export const updateCompanyApi = async (id: string | number, payload: TCreateCompanyReqDto): Promise<TCreateCompanyResDto> => {
  const formData = processDataBeforeCallApi(payload);
  const rs = await clientApi.post<TCreateCompanyResDto>(`/auth/company/${id}`, formData);
  return rs.data;
};

export const updateLogoApi = async (id: string | number, companyLogoFile?: File | null | undefined) => {
  const formData = new FormData();
  formData.append("files", companyLogoFile ?? "");
  const rs = await clientApi.patch<TCreateCompanyResDto>(`/auth/company/${id}/save-logo`, formData);
  return rs.data;
};

export const createCompanyApi = async (payload: TCreateCompanyReqDto): Promise<TCreateCompanyResDto> => {
  const formData = processDataBeforeCallApi(payload);
  const rs = await clientApi.post<TCreateCompanyResDto>("/auth/company", formData);
  return rs.data;
};

export const fetchCompanyInfoApi = async (id: string | number): Promise<TGetCompanyResDto> => {
  const { data } = await clientApi.get<TGetCompanyResDto>(`/auth/company/${id}`);
  return data;
};

export const useCompanyInfo = (id?: number) => {
  const { companyId } = useParams();

  const fetchId = id ?? companyId;

  const isValidId = useMemo(() => {
    if (!fetchId) return false;
    const idNumber = Number(fetchId);
    return !isNaN(idNumber) && Number.isInteger(idNumber) && idNumber > 0;
  }, [fetchId]);

  return useQuery({
    queryKey: [QUERY_KEYS.COMPANY.FETCH_DETAIL, fetchId],
    queryFn: async () => {
      return fetchId ? await fetchCompanyInfoApi(fetchId) : undefined;
    },
    enabled: isValidId,
  });
};

export const validateImportCompanyApi = async (payload: TCompanyRowExcel[]) => {
  const rs = await clientApi.post("/auth/company/validate-import", payload);
  return rs.data;
};

export const importCompanyApi = async (payload: TGetCompanyOutputDto[]) => {
  const rs = await clientApi.post("/auth/company/import", payload);
  return rs.data;
};

export const setCompanyArchive = async (companyId: number, isArchive: boolean = false) => {
  const rs = await clientApi.patch(`/auth/company/archive/${companyId}`, {
    isArchive,
  });
  return rs.data;
};

export const getCompanyExists = async (
  typeCheck: string,
  companyName: string,
  representativeName?: string | null,
  address?: string | null,
  companyId?: string,
) => {
  const rs = await clientApi.get("/auth/company/exist-company-name", {
    params: { typeCheck, companyName, representativeName, address, companyId },
  });
  return rs.data;
};

export const getActiveJobsByCompany = async (companyId: string) => {
  const rs = await clientApi.get(`/auth/company/${companyId}/get-active-jobs`);
  return rs.data;
};

export const fetchSignedUrlApi = async (filePath: string | null | undefined): Promise<TGetSingedUrlResDto> => {
  const url = createUrlWithParams("/files/signed-url", { filePath });
  const rs = await globalApi.get(url);
  return rs.data;
};

export const useCompanyFileSignedUrl = (filePath?: string | null) => {
  return useQuery({
    queryKey: [QUERY_KEYS.FILE.FETCH_COMPANY_SIGNED_URL, filePath],
    queryFn: () => (filePath ? fetchSignedUrlApi(filePath) : undefined),
    enabled: Boolean(filePath),
  });
};
